import * as THREE from "three";
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import Engine from './Engine';
import Input from './Input';
import WorkspaceManager from './WorkspaceManager';
import { duplicateObjects } from './ShortcutHandler';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import TransformUtils from './utilities/TransformUtils';
import MathUtils from './utilities/MathUtils';
import store from '../state/store';
import { CustomEventTypes, RenderLayers, GizmoModes, GizmoPivotMode, TimelineEffectorTypes } from '../globals';
import SetObjectScaleAction from '../actions/SetObjectScaleAction';
import SetObjectRotationAction from '../actions/SetObjectRotationAction';
import SetObjectPositionAction from '../actions/SetObjectPositionAction';
import { KeyCodes, ModifierKeyCodes } from '../interfaces/Input';
/**
 * @author arodic / https://github.com/arodic
 */

const _p = new THREE.Vector3();
const _r = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _m = new THREE.Matrix4();
class TransformControls extends THREE.Object3D
{
	constructor()
	{
		super();

		this.visible = false;

		const _gizmo = new TransformControlsGizmo();
		this.add(_gizmo);

		const _plane = new TransformControlsPlane();
		this.add(_plane);

		const scope = this;

		this.dummyObject = new THREE.Object3D("gizmoDummy");
		this.objects = [];

		this.initialWorldPositions = [];
		this.initialPositions = [];
		this.initialRotations = [];
		this.initialScales = [];
		this.gridSnapMode = false;
		this.freeMoveMode = false;
		this.quickDuplicationEnabled = true;

		// Define properties with getters/setter
		// Setting the defined property will automatically trigger change event
		// Defined properties are passed down to gizmo and plane

		defineProperty("camera", undefined);
		defineProperty("object", undefined);
		defineProperty("enabled", true);
		defineProperty("axis", null);
		defineProperty("mode", "translate");
		defineProperty("translationSnap", null);
		defineProperty("rotationSnap", null);
		defineProperty("scaleSnap", null);
		defineProperty("space", "world");
		defineProperty("pivotMode", "group");
		defineProperty("size", 1);
		defineProperty("dragging", false);
		defineProperty("showX", true);
		defineProperty("showY", true);
		defineProperty("showZ", true);

		const changeEvent = { type: "change" };
		const mouseDownEvent = { type: "mouseDown" };
		const mouseUpEvent = { type: "mouseUp", mode: scope.mode };
		const objectChangeEvent = { type: "objectChange" };
		// Reusable utility variables

		const ray = new THREE.Raycaster();
		ray.layers.enable(RenderLayers.MainCameraOnly);

		const _tempVector = new THREE.Vector3();
		const _tempVector2 = new THREE.Vector3();
		const _tempQuaternion = new THREE.Quaternion();
		const _unit = {
			X: new THREE.Vector3(1, 0, 0),
			Y: new THREE.Vector3(0, 1, 0),
			Z: new THREE.Vector3(0, 0, 1)
		};

		const pointStart = new THREE.Vector3();
		const pointEnd = new THREE.Vector3();
		const offset = new THREE.Vector3();
		const lastPointerPosition = new THREE.Vector2();

		const rotationAxis = new THREE.Vector3();
		const startNorm = new THREE.Vector3();
		const endNorm = new THREE.Vector3();
		let rotationAngle = 0;

		const cameraPosition = new THREE.Vector3();
		const cameraQuaternion = new THREE.Quaternion();
		const cameraScale = new THREE.Vector3();

		const parentPosition = new THREE.Vector3();
		const parentQuaternion = new THREE.Quaternion();
		const parentQuaternionInv = new THREE.Quaternion();
		const parentScale = new THREE.Vector3();

		const worldPositionStart = new THREE.Vector3();
		const worldQuaternionStart = new THREE.Quaternion();
		const worldScaleStart = new THREE.Vector3();

		const worldPosition = new THREE.Vector3();
		const worldQuaternion = new THREE.Quaternion();
		const worldQuaternionInv = new THREE.Quaternion();
		const worldScale = new THREE.Vector3();

		const eye = new THREE.Vector3();

		const positionStart = new THREE.Vector3();
		const quaternionStart = new THREE.Quaternion();
		const eulerStart = new THREE.Euler();
		const scaleStart = new THREE.Vector3();

		// TODO: remove properties unused in plane and gizmo

		defineProperty("worldPosition", worldPosition);
		defineProperty("worldPositionStart", worldPositionStart);
		defineProperty("worldQuaternion", worldQuaternion);
		defineProperty("worldQuaternionStart", worldQuaternionStart);
		defineProperty("cameraPosition", cameraPosition);
		defineProperty("cameraQuaternion", cameraQuaternion);
		defineProperty("pointStart", pointStart);
		defineProperty("pointEnd", pointEnd);
		defineProperty("rotationAxis", rotationAxis);
		defineProperty("rotationAngle", rotationAngle);
		defineProperty("eye", eye);

		this.dispose = function()
		{
			this.traverse(function(child)
			{
				if (child.geometry) child.geometry.dispose();
				if (child.material) child.material.dispose();
			});
		};

		this.setGridSnapMode = function(value)
		{
			this.gridSnapMode = value;
		};

		this.enterFreeMoveMode = function(mouseDownCallback, mouseMoveCallback, mouseUpCallback, initialGizmoOffset)
		{
			this.freeMoveMode = true;
			this.onFreeMoveModeMouseDownCallback = mouseDownCallback;
			this.onFreeMoveModeMouseMoveCallback = mouseMoveCallback;
			this.onFreeMoveModeMouseUpCallback = mouseUpCallback;
			this.freeMoveInitialOffset = initialGizmoOffset;
			if(this.freeMoveInitialOffset) this.dummyObject.position.add(this.freeMoveInitialOffset);

			_gizmo.toggleDashedLines(true);
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onGizmoFreeMoveModeChanged));
		};

		this.onFreeMoveMouseDown = function()
		{
			if(this.onFreeMoveModeMouseDownCallback) this.onFreeMoveModeMouseDownCallback();
		};

		this.onFreeModeMouseMove = function(delta)
		{
			delta.x = Math.abs(delta.x) > 0.00001 ? delta.x : 0;
			delta.y = Math.abs(delta.y) > 0.00001 ? delta.y : 0;
			delta.z = Math.abs(delta.z) > 0.00001 ? delta.z : 0;
			if(this.onFreeMoveModeMouseMoveCallback)
			{
				this.onFreeMoveModeMouseMoveCallback(
					{	x : delta.x,
						y : delta.y,
						z : delta.z});
			}
		};

		this.onFreeModeMouseUp = function(delta)
		{
			if(this.onFreeMoveModeMouseUpCallback) this.onFreeMoveModeMouseUpCallback();
		};

		this.exitFreeMoveMode = function()
		{
			if(this.onFreeMoveModeMouseUp) this.onFreeMoveModeMousUp();
			this.freeMoveMode = false;
			_gizmo.toggleDashedLines(false);
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onGizmoFreeMoveModeChanged));
		};

		this.setObjects = function(objects)
		{
			if (this.dragging && !this.copyArmed)
			{
				this.pointerUp();
			}

			this.objects = [];
			if (objects.length > 0)
			{
				for (const object of objects)
				{
					let parentIsSelected = false;
					for (const parent of objects)
					{
						if (SceneObjectUtils.isDescendantOf(object, parent))
						{
							parentIsSelected = true;
							break;
						}
					}
					if (!parentIsSelected)
					{
						this.objects.push(object);
					}
				}

				this.setDummyTransform();
				if (this.mode !== 'hidden')
				{
					this.visible = true;
				}

			}
			else
			{
				this.visible = false;
				this.axis = null;
			}
		};

		this.setDummyTransform = function()
		{
			if(this.freeMoveMode)
			{
				this.exitFreeMoveMode();
			}

			if (this.objects.length === 0 || this.dragging) return;

			const currentSelection = Engine.getInstance().getPickingManager().getCurrentSelection();
			const isGroupSelected = (currentSelection.groupGuid !== undefined);
			if (isGroupSelected) this.pivotMode = GizmoPivotMode.Group;
			if (this.pivotMode === GizmoPivotMode.Group)
			{
				const avgPosition = new THREE.Vector3();
				for (const object of this.objects)
				{
					object.updateMatrixWorld();
					object.getWorldPosition(_p);
					avgPosition.add(_p);
				}

				avgPosition.divideScalar(this.objects.length);
				this.dummyObject.position.set(avgPosition.x, avgPosition.y, avgPosition.z);
				const lastObject = this.objects[this.objects.length - 1];
				lastObject.getWorldQuaternion(this.dummyObject.quaternion);
			}
			else
			{
				const firstObject = this.objects[0];
				firstObject.getWorldPosition(this.dummyObject.position);
				firstObject.getWorldQuaternion(this.dummyObject.quaternion);
			}
		};

		// Defined getter, setter and store for a property
		function defineProperty(propName, defaultValue)
		{
			let propValue = defaultValue;

			Object.defineProperty(scope, propName, {
				get: function()
				{

					return propValue !== undefined ? propValue : defaultValue;

				},
				set: function(value)
				{
					if (propValue !== value)
					{
						propValue = value;
						_plane[propName] = value;
						_gizmo[propName] = value;

						scope.dispatchEvent({ type: propName + "-changed", value: value });
						scope.dispatchEvent(changeEvent);
					}
				}
			});

			scope[propName] = defaultValue;
			_plane[propName] = defaultValue;
			_gizmo[propName] = defaultValue;
		}

		// updateMatrixWorld  updates key transformation variables
		this.updateMatrixWorld = function()
		{
			if (this.visible)
			{
				this.dummyObject.updateMatrixWorld();
				this.dummyObject.parent.matrixWorld.decompose(parentPosition, parentQuaternion, parentScale);
				this.dummyObject.matrixWorld.decompose(worldPosition, worldQuaternion, worldScale);

				parentQuaternionInv.copy(parentQuaternion).invert();
				worldQuaternionInv.copy(worldQuaternion).invert();
			}

			if(this.camera)
			{
				this.camera.updateMatrixWorld();
				this.camera.matrixWorld.decompose(cameraPosition, cameraQuaternion, cameraScale);
			}


			eye.copy(cameraPosition).sub(worldPosition).normalize();

			THREE.Object3D.prototype.updateMatrixWorld.call(this);
		};

		this.pointerHover = function(pointer)
		{
			if (!this.visible || this.dragging === true || (pointer.button !== undefined && pointer.button !== 0))
			{return;}

			ray.setFromCamera(pointer, this.camera);

			const intersect = ray.intersectObjects(_gizmo.picker[this.mode].children, true)[0] || false;

			if (intersect)
			{
				this.axis = intersect.object.name;
			}
			else
			{
				this.axis = null;
			}
		};

		this.pointerDown = function(pointer)
		{
			if (!this.visible || this.dragging === true) return;
			if (this.axis !== null)
			{
				ray.setFromCamera(pointer, this.camera);

				const planeIntersect = ray.intersectObjects([_plane], true)[0] || false;

				if (planeIntersect)
				{
					let space = this.space;

					if (this.mode === 'scale')
					{
						space = 'local';
					}
					else if (this.axis === 'E' || this.axis === 'XYZE' || this.axis === 'XYZ')
					{
						space = 'world';
					}

					if (space === 'local' && this.mode === 'rotate')
					{
						const snap = this.rotationSnap;

						if (this.axis === 'X' && snap) this.dummyObject.rotation.x = Math.round(this.dummyObject.rotation.x / snap) * snap;
						if (this.axis === 'Y' && snap) this.dummyObject.rotation.y = Math.round(this.dummyObject.rotation.y / snap) * snap;
						if (this.axis === 'Z' && snap) this.dummyObject.rotation.z = Math.round(this.dummyObject.rotation.z / snap) * snap;
					}

					this.dummyObject.updateMatrixWorld();
					this.dummyObject.parent.updateMatrixWorld();
					this.dummyObject.scale.set(1, 1, 1);
					rotationAxis.set(0, 0, 0);
					rotationAngle = 0.0;
					positionStart.copy(this.dummyObject.position);
					quaternionStart.copy(this.dummyObject.quaternion);
					eulerStart.copy(this.dummyObject.rotation);
					scaleStart.copy(this.dummyObject.scale);

					this.dummyObject.matrixWorld.decompose(worldPositionStart, worldQuaternionStart, worldScaleStart);
					pointStart.copy(planeIntersect.point).sub(worldPositionStart);
					MathUtils.round(worldPositionStart);

				}
				this.initialWorldPositions.length = 0;
				this.initialPositions.length = 0;
				this.initialRotations.length = 0;
				this.initialScales.length = 0;

				for (const object of this.objects)
				{
					object.updateMatrixWorld();
					const worldPos = new THREE.Vector3();
					object.getWorldPosition(worldPos);
					this.initialWorldPositions.push(worldPos);
					this.initialPositions.push(new THREE.Vector3().copy(object.position));
					this.initialRotations.push(new THREE.Euler().copy(object.rotation));
					this.initialScales.push(new THREE.Vector3().copy(object.scale));
				}

				if(this.freeMoveMode)
				{
					this.onFreeMoveMouseDown();
				}

				// if only SHIFT is held down at the time of gizmo interaction, we prepare for object copy
				this.copyArmed = Input.getModifierExact(ModifierKeyCodes.SHIFT);
				this.dragging = true;
				mouseDownEvent.mode = this.mode;
				this.dispatchEvent(mouseDownEvent);
				lastPointerPosition.set(pointer.x, pointer.y);
			}
		};

		this.pointerMove = function(pointer)
		{
			if (lastPointerPosition.x === pointer.x && lastPointerPosition.y === pointer.y) return;

			// only copy if armed and still holding SHIFT
			if(this.quickDuplicationEnabled && this.copyArmed && Input.getModifierExact(ModifierKeyCodes.SHIFT))
			{
				duplicateObjects(true);
				this.copyArmed = false;
			}

			lastPointerPosition.set(pointer.x, pointer.y);

			const axis = this.axis;
			const mode = this.mode;
			let space = this.space;

			if (mode === 'scale')
			{
				space = 'local';
			}
			else if (axis === 'E' || axis === 'XYZE' || axis === 'XYZ')
			{
				space = 'world';
			}

			if (!this.visible || axis === null || this.dragging === false) return;

			ray.setFromCamera(pointer, this.camera);

			const planeIntersect = ray.intersectObjects([_plane], true)[0] || false;

			if (planeIntersect === false) return;

			pointEnd.copy(planeIntersect.point).sub(worldPositionStart);
			if (mode === 'translate')
			{
			// Apply translate
				offset.copy(pointEnd).sub(pointStart);

				if (space === 'local' && axis !== 'XYZ')
				{
					offset.applyQuaternion(worldQuaternionInv);
				}

				if (axis.indexOf('X') === - 1) offset.x = 0;
				if (axis.indexOf('Y') === - 1) offset.y = 0;
				if (axis.indexOf('Z') === - 1) offset.z = 0;

				if (space === 'local' && axis !== 'XYZ')
				{
					offset.applyQuaternion(quaternionStart).divide(parentScale);
				}
				else
				{
					offset.applyQuaternion(parentQuaternionInv).divide(parentScale);
				}
				MathUtils.round(offset);

				this.dummyObject.position.copy(offset).add(positionStart);
				// Apply translation snap
				if (this.translationSnap)
				{
					if (space === 'local')
					{
						this.dummyObject.position.applyQuaternion(_tempQuaternion.copy(quaternionStart).invert());

						if (axis.search('X') !== -1)
						{
							this.dummyObject.position.x = Math.round(this.dummyObject.position.x / this.translationSnap) * this.translationSnap;
							if (!this.gridSnapMode) this.dummyObject.position.x += positionStart.x % 1;
						}

						if (axis.search('Y') !== -1)
						{
							this.dummyObject.position.y = Math.round(this.dummyObject.position.y / this.translationSnap) * this.translationSnap;
							if (!this.gridSnapMode) this.dummyObject.position.y += positionStart.y % 1;
						}

						if (axis.search('Z') !== -1)
						{
							this.dummyObject.position.z = Math.round(this.dummyObject.position.z / this.translationSnap) * this.translationSnap;
							if (!this.gridSnapMode) this.dummyObject.position.z += positionStart.z % 1;
						}

						this.dummyObject.position.applyQuaternion(quaternionStart);
					}

					if (space === 'world')
					{
						if (this.dummyObject.parent)
						{
							this.dummyObject.position.add(_tempVector.setFromMatrixPosition(this.dummyObject.parent.matrixWorld));

						}

						if (axis.search('X') !== - 1)
						{
							this.dummyObject.position.x = this.gridSnapMode ? Math.floor(this.dummyObject.position.x) : offset.x - (offset.x % this.translationSnap) + worldPositionStart.x;
						}
						if (axis.search('Y') !== - 1)
						{
							this.dummyObject.position.y = this.gridSnapMode ? Math.floor(this.dummyObject.position.y) : offset.y - (offset.y % this.translationSnap) + worldPositionStart.y;
						}

						if (axis.search('Z') !== - 1)
						{
							this.dummyObject.position.z = this.gridSnapMode ? Math.floor(this.dummyObject.position.z) : offset.z - (offset.z % this.translationSnap) + worldPositionStart.z;
						}

						if (this.dummyObject.parent)
						{
							this.dummyObject.position.sub(_tempVector.setFromMatrixPosition(this.dummyObject.parent.matrixWorld));
						}
					}
				}

				this.dummyObject.updateMatrixWorld(true);
				if(this.freeMoveMode)
				{
					_p.subVectors(this.dummyObject.position, worldPositionStart);
					this.onFreeModeMouseMove(_p);
				}
				else
				{
					if (this.pivotMode === GizmoPivotMode.Object && this.objects.length > 1 && this.space === 'local')
					{
						for (let i = 0; i < this.objects.length; i++)
						{
							const object = this.objects[i];
							_p.subVectors(this.dummyObject.position, worldPositionStart);
							_r.copy(this.dummyObject.quaternion).invert();
							_p.applyQuaternion(_r);
							object.getWorldQuaternion(_r);
							_p.applyQuaternion(_r);
							_tempVector.addVectors(this.initialWorldPositions[i], _p);
							TransformUtils.setWorldPosition(object, _tempVector);
						}
					}
					else
					{
						_p.subVectors(this.dummyObject.position, worldPositionStart);
						for (let i = 0; i < this.objects.length; i++)
						{
							const object = this.objects[i];
							_tempVector.addVectors(this.initialWorldPositions[i], _p);
							TransformUtils.setWorldPosition(object, _tempVector);
						}
					}
					document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectPositionChanged, { detail: { sender: this } }));
				}
			}
			else if (mode === 'scale')
			{
				if (axis.search('XYZ') !== - 1)
				{
					let d = pointEnd.length() / pointStart.length();

					if (pointEnd.dot(pointStart) < 0) d *= - 1;

					_tempVector2.set(d, d, d);
				}
				else
				{
					_tempVector.copy(pointStart);
					_tempVector2.copy(pointEnd);

					_tempVector.applyQuaternion(worldQuaternionInv);
					_tempVector2.applyQuaternion(worldQuaternionInv);

					_tempVector2.divide(_tempVector);

					if (axis.search('X') === - 1)
					{
						_tempVector2.x = 1;
					}
					if (axis.search('Y') === - 1)
					{
						_tempVector2.y = 1;
					}
					if (axis.search('Z') === - 1)
					{
						_tempVector2.z = 1;
					}

					if (axis.length === 2 && !Input.getModifier(ModifierKeyCodes.SHIFT))
					{
						switch (axis)
						{
							case 'XZ':
								_tempVector2.x = Math.max(_tempVector2.x, _tempVector2.z);
								_tempVector2.z = Math.max(_tempVector2.x, _tempVector2.z);
								break;
							case 'XY':
								_tempVector2.x = Math.max(_tempVector2.x, _tempVector2.y);
								_tempVector2.y = Math.max(_tempVector2.x, _tempVector2.y);
								break;
							case 'YZ':
								_tempVector2.y = Math.max(_tempVector2.y, _tempVector2.z);
								_tempVector2.z = Math.max(_tempVector2.y, _tempVector2.z);
								break;

						}
					}
				}

				// Apply scale
				this.dummyObject.scale.copy(scaleStart).multiply(_tempVector2);

				if (this.scaleSnap)
				{
					if (axis.search('X') !== - 1)
					{
						this.dummyObject.scale.x = Math.round( this.dummyObject.scale.x / this.scaleSnap ) * this.scaleSnap || this.scaleSnap;
					}

					if (axis.search('Y') !== - 1)
					{
						this.dummyObject.scale.y = Math.round( this.dummyObject.scale.y / this.scaleSnap ) * this.scaleSnap || this.scaleSnap;
					}

					if (axis.search('Z') !== - 1)
					{
						this.dummyObject.scale.z = Math.round( this.dummyObject.scale.z / this.scaleSnap ) * this.scaleSnap || this.scaleSnap;
					}
				}



				for (let i = 0; i < this.objects.length; i++)
				{
					const object = this.objects[i];
					object.scale.copy(this.initialScales[i]).multiply(this.dummyObject.scale);
				}
				document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectScaleChanged, { detail: { sender: this } }));

			}
			else if (mode === 'rotate')
			{
				offset.copy(pointEnd).sub(pointStart);

				const ROTATION_SPEED = 20 / worldPosition.distanceTo(_tempVector.setFromMatrixPosition(this.camera.matrixWorld));

				if (axis === 'E')
				{
					rotationAxis.copy(eye);
					rotationAngle = pointEnd.angleTo(pointStart);

					startNorm.copy(pointStart).normalize();
					endNorm.copy(pointEnd).normalize();

					rotationAngle *= (endNorm.cross(startNorm).dot(eye) < 0 ? 1 : - 1);
				}
				else if (axis === 'XYZE')
				{
					rotationAxis.copy(offset).cross(eye).normalize();
					rotationAngle = offset.dot(_tempVector.copy(rotationAxis).cross(this.eye)) * ROTATION_SPEED;
				}
				else if (axis === 'X' || axis === 'Y' || axis === 'Z')
				{
					rotationAxis.copy(_unit[axis]);
					_tempVector.copy(_unit[axis]);

					if (space === 'local')
					{
						_tempVector.applyQuaternion(worldQuaternion);
					}

					rotationAngle = offset.dot(_tempVector.cross(eye).normalize()) * ROTATION_SPEED;
				}

				// Apply rotation snap
				if (this.rotationSnap)
				{rotationAngle = Math.round(rotationAngle / this.rotationSnap) * this.rotationSnap;}

				this.rotationAngle = rotationAngle;

				// Apply rotate
				if (space === 'local' && axis !== 'E' && axis !== 'XYZE')
				{
					this.dummyObject.quaternion.copy(quaternionStart);
					this.dummyObject.quaternion.multiply(_tempQuaternion.setFromAxisAngle(rotationAxis, rotationAngle)).normalize();
				}
				else
				{
					rotationAxis.applyQuaternion(parentQuaternionInv);
					this.dummyObject.quaternion.copy(_tempQuaternion.setFromAxisAngle(rotationAxis, rotationAngle));
					this.dummyObject.quaternion.multiply(quaternionStart).normalize();
				}

				this.dummyObject.updateMatrixWorld(true);
				if (this.pivotMode === GizmoPivotMode.Group && this.objects.length > 1) //rotate around pivot
				{
					_p.copy(rotationAxis);
					if (this.space === 'local') _p.applyQuaternion(this.dummyObject.quaternion);

					for (let i = 0; i < this.objects.length; i++)
					{
						const object = this.objects[i];
						object.position.copy(this.initialPositions[i]);
						object.rotation.copy(this.initialRotations[i]);

						TransformUtils.rotateAround(object, this.dummyObject.position, _p, rotationAngle);
					}
				}
				else //rotate around self
				{
					for (let i = 0; i < this.objects.length; i++)
					{
						const object = this.objects[i];
						object.rotation.copy(this.initialRotations[i]);

						_p.copy(rotationAxis);
						if (this.space === 'local')
						{
							if (this.pivotMode === GizmoPivotMode.Object)
							{
								object.getWorldQuaternion(_r);
								_p.applyQuaternion(_r);
							}
							else
							{
								_p.applyQuaternion(this.dummyObject.quaternion);
							}
						}

						object.parent.getWorldQuaternion(_r);
						_m.makeRotationFromQuaternion(_r);
						_m.invert();
						_p.applyMatrix4(_m);

						_r.setFromAxisAngle(_p, rotationAngle);
						object.quaternion.premultiply(_r);
					}
				}

				document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectRotationChanged, { detail: { sender: this } }));
			}

			this.dispatchEvent(changeEvent);
			this.dispatchEvent(objectChangeEvent);

		};

		this.pointerUp = function()
		{
			if (this.dragging && (this.axis !== null))
			{
				mouseUpEvent.mode = this.mode;
				this.dispatchEvent(mouseUpEvent);
			}

			if (this.dragging)
			{
				this.updateMatrixWorld();
				const isRecording = store.getState().timelineModule.editorData.isRecording;
				const raiseTemporaryAction = isRecording || WorkspaceManager.getModelTrackerPoseEditorData().context;
				const recordEventDetail = { };

				switch (scope.mode)
				{
					case "translate":
						{
							if(this.freeMoveMode)
							{
								this.onFreeModeMouseUp();
							}
							else
							{
								const newPositions = [];
								for (let i = 0; i < this.objects.length; i++)
								{
									const object = this.objects[i];
									newPositions.push(new THREE.Vector3().copy(object.position));
									object.position.copy(this.initialPositions[i]);
								}

								const action = new SetObjectPositionAction(this.objects, newPositions, [...this.initialPositions]);
								if(raiseTemporaryAction) action.flagAsTemporary();

								if (isRecording)
								{
									recordEventDetail.animationType = TimelineEffectorTypes.TransformTranslate;
									recordEventDetail.space = this.space;
								}
							}
						}
						break;
					case "rotate":
						{
							const newRotations = [];
							const newPositions = [];
							for (let i = 0; i < this.objects.length; i++)
							{
								const object = this.objects[i];
								newRotations.push(new THREE.Euler().copy(object.rotation));
								newPositions.push(new THREE.Vector3().copy(object.position));
								object.rotation.copy(this.initialRotations[i]);
								object.position.copy(this.initialPositions[i]);
							}

							const action = new SetObjectRotationAction(this.objects, newRotations, [...this.initialRotations], newPositions, [...this.initialPositions]);
							if(raiseTemporaryAction) action.flagAsTemporary();

							if (isRecording)
							{
								if (this.pivotMode === GizmoPivotMode.Group && this.objects.length > 1)
								{
									if (this.space === 'local') rotationAxis.applyQuaternion(this.dummyObject.quaternion);

									if ( Math.abs(rotationAxis.x) <= 0.001) rotationAxis.x = 0;
									if ( Math.abs(rotationAxis.y) <= 0.001) rotationAxis.y = 0;
									if ( Math.abs(rotationAxis.z) <= 0.001) rotationAxis.z = 0;
									rotationAxis.normalize();
									recordEventDetail.rotationAngle = rotationAngle * 180.0 / Math.PI;
									recordEventDetail.rotationAxis = {	x: rotationAxis.x, y: rotationAxis.y, z: rotationAxis.z };
									recordEventDetail.rotationPivot = {	x: worldPosition.x, y: worldPosition.y, z: worldPosition.z };
									recordEventDetail.animationType = TimelineEffectorTypes.TransformRotatePivot;
									recordEventDetail.associatedAction = action;
								}
								else
								{
									recordEventDetail.animationType = TimelineEffectorTypes.TransformRotateSelf;
								}
							}

						}
						break;
					case "scale":
						{
							const newScales = [];
							const newPositions = [];
							for (let i = 0; i < this.objects.length; i++)
							{
								const object = this.objects[i];
								newScales.push(new THREE.Vector3().copy(object.scale));
								newPositions.push(new THREE.Vector3().copy(object.position));
								object.scale.copy(this.initialScales[i]);
								object.position.copy(this.initialPositions[i]);
							}

							const action = new SetObjectScaleAction(this.objects, newScales, [...this.initialScales], newPositions, [...this.initialPositions]);
							if(raiseTemporaryAction) action.flagAsTemporary();

							if (isRecording)
							{
								recordEventDetail.animationType = TimelineEffectorTypes.TransformScale;
							}

						}
						break;
					default:
						break;
				}

				if (isRecording)
				{
					document.dispatchEvent(new CustomEvent(CustomEventTypes.onTransformRecordEvent, { detail: recordEventDetail }));
				}
			}

			this.dragging = false;
		};

		this.getMode = function()
		{
			return scope.mode;
		};

		this.setMode = function(mode)
		{
			if(this.freeMoveMode)
			{
				this.setDummyTransform();
			}

			if (scope.mode === GizmoModes.Hidden && mode !== GizmoModes.Hidden && this.objects.length > 0)
			{
				scope.visible = true;
			}

			scope.mode = mode;

			if (scope.mode === GizmoModes.Hidden)
			{
				scope.visible = false;
			}

			this.dummyObject.matrixWorld.decompose(worldPositionStart, worldQuaternionStart, worldScaleStart);
			MathUtils.round(worldPositionStart);
		};

		this.setTranslationSnap = function(translationSnap)
		{
			scope.translationSnap = translationSnap;
		};

		this.setRotationSnap = function(rotationSnap)
		{
			scope.rotationSnap = rotationSnap;
		};

		this.setScaleSnap = function(scaleSnap)
		{
			scope.scaleSnap = scaleSnap;
		};

		this.setSize = function(size)
		{
			scope.size = size;
		};

		this.setSpace = function(space)
		{
			scope.space = space;
		};

		this.setPivotMode = function(pivotMode)
		{
			scope.pivotMode = pivotMode;
			scope.setDummyTransform();
		};

		this.update = function()
		{
			console.warn('THREE.TransformControls: update function has no more functionality and therefore has been deprecated.');
		};
	}
}

TransformControls.prototype.isTransformControls = true;

class TransformControlsGizmo extends THREE.Object3D
{
	constructor()
	{
		super();

		this.type = 'TransformControlsGizmo';

		// shared materials

		const gizmoMaterial = new THREE.MeshBasicMaterial({
			depthTest: false,
			depthWrite: false,
			transparent: true,
			side: THREE.DoubleSide,
			fog: false
		});

		const gizmoLineMaterial = new THREE.LineBasicMaterial({
			depthTest: false,
			depthWrite: false,
			transparent: true,
			linewidth: 1,
			fog: false
		});

		const dashedGizmoLineMaterial = new LineMaterial({
			depthTest: false,
			depthWrite: false,
			transparent: true,
			fog: false,

			linewidth: 2,
			resolution: new THREE.Vector2( window.innerWidth, window.innerHeight ),
			dashed: false,
			dashSize: 0.10,
			gapSize: 0.075,
			//dashOffset: 0.05,

		});

		// Make unique material for each axis/color

		const matInvisible = gizmoMaterial.clone();
		matInvisible.opacity = 0.15;

		const matHelper = gizmoMaterial.clone();
		matHelper.opacity = 0.33;

		const matHelperRotation = matHelper.clone();

		const matRed = gizmoMaterial.clone();
		matRed.color.set(0xff0000);

		const matGreen = gizmoMaterial.clone();
		matGreen.color.set(0x00ff00);

		const matBlue = gizmoMaterial.clone();
		matBlue.color.set(0x0000ff);

		const matWhiteTransparent = gizmoMaterial.clone();
		matWhiteTransparent.opacity = 0.25;

		const matYellowTransparent = matWhiteTransparent.clone();
		matYellowTransparent.color.set(0xffff00);

		const matCyanTransparent = matWhiteTransparent.clone();
		matCyanTransparent.color.set(0x00ffff);

		const matMagentaTransparent = matWhiteTransparent.clone();
		matMagentaTransparent.color.set(0xff00ff);

		const matYellow = gizmoMaterial.clone();
		matYellow.color.set(0xffff00);

		const matLineRed = gizmoLineMaterial.clone();
		matLineRed.color.set(0xff0000);

		const matLineGreen = gizmoLineMaterial.clone();
		matLineGreen.color.set(0x00ff00);

		const matLineBlue = gizmoLineMaterial.clone();
		matLineBlue.color.set(0x0000ff);

		const matLineRedDashed = dashedGizmoLineMaterial.clone();
		matLineRedDashed.color.set(0xff0000);

		const matLineGreenDashed = dashedGizmoLineMaterial.clone();
		matLineGreenDashed.color.set(0x00ff00);

		const matLineBlueDashed = dashedGizmoLineMaterial.clone();
		matLineBlueDashed.color.set(0x0000ff);

		const matLineCyan = gizmoLineMaterial.clone();
		matLineCyan.color.set(0x00ffff);

		const matLineMagenta = gizmoLineMaterial.clone();
		matLineMagenta.color.set(0xff00ff);

		const matLineYellow = gizmoLineMaterial.clone();
		matLineYellow.color.set(0xffff00);

		const matLineGray = gizmoLineMaterial.clone();
		matLineGray.color.set(0x787878);

		const matLineYellowTransparent = matLineYellow.clone();
		matLineYellowTransparent.opacity = 0.25;

		// reusable geometry

		const arrowGeometry = new THREE.CylinderGeometry(0, 0.05, 0.2, 12, 1, false);

		const scaleHandleGeometry = new THREE.BoxGeometry(0.125, 0.125, 0.125);

		const lineGeometry = new THREE.BufferGeometry();
		lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 1, 0, 0], 3));

		const dashedLineGeometry = new LineGeometry();
		dashedLineGeometry.setPositions([0, 0, 0, 1, 0, 0]);

		const dashedGizmoLine = new Line2(dashedLineGeometry); //only need to have an instance to call computeLineDistances upon
		dashedGizmoLine.computeLineDistances();

		const CircleGeometry = function(radius, arc)
		{
			const geometry = new THREE.BufferGeometry();
			const vertices = [];

			for (let i = 0; i <= 64 * arc; ++i)
			{
				vertices.push(0, Math.cos(i / 32 * Math.PI) * radius, Math.sin(i / 32 * Math.PI) * radius);
			}

			geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

			return geometry;
		};

		// Special geometry for transform helper. If scaled with position vector it spans from [0,0,0] to position

		// var TranslateHelperGeometry = function ()
		// {

		// 	var geometry = new THREE.BufferGeometry();

		// 	geometry.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 1, 1, 1], 3));

		// 	return geometry;

		// };

		// Gizmo definitions - custom hierarchy definitions for setupGizmo() function


		const gizmoTranslate = {
			X: [
				[new THREE.Mesh(arrowGeometry, matRed), [1, 0, 0], [0, 0, - Math.PI / 2], null, 'fwd'],
				//[new THREE.Mesh(arrowGeometry, matRed), [1, 0, 0], [0, 0, Math.PI / 2], null, 'bwd'],
				[new Line2(dashedLineGeometry, matLineRedDashed)]
			],
			Y: [
				[new THREE.Mesh(arrowGeometry, matGreen), [0, 1, 0], null, null, 'fwd'],
				//[new THREE.Mesh(arrowGeometry, matGreen), [0, 1, 0], [Math.PI, 0, 0], null, 'bwd'],
				[new Line2(dashedLineGeometry, matLineGreenDashed), null, [0, 0, Math.PI / 2]]
			],
			Z: [
				[new THREE.Mesh(arrowGeometry, matBlue), [0, 0, 1], [Math.PI / 2, 0, 0], null, 'fwd'],
				//[new THREE.Mesh(arrowGeometry, matBlue), [0, 0, 1], [- Math.PI / 2, 0, 0], null, 'bwd'],
				[new Line2(dashedLineGeometry, matLineBlueDashed), null, [0, - Math.PI / 2, 0]]
			],
			XYZ: [
				// [new THREE.Mesh(new THREE.OctahedroGeometry(0.1, 0), matWhiteTransparent.clone()), [0, 0, 0], [0, 0, 0]]
			],
			XY: [
				[new THREE.Mesh(new THREE.PlaneGeometry(0.295, 0.295), matYellowTransparent.clone()), [0.15, 0.15, 0]],
				[new THREE.Line(lineGeometry, matLineYellow), [0.18, 0.3, 0], null, [0.125, 1, 1]],
				[new THREE.Line(lineGeometry, matLineYellow), [0.3, 0.18, 0], [0, 0, Math.PI / 2], [0.125, 1, 1]]
			],
			YZ: [
				[new THREE.Mesh(new THREE.PlaneGeometry(0.295, 0.295), matCyanTransparent.clone()), [0, 0.15, 0.15], [0, Math.PI / 2, 0]],
				[new THREE.Line(lineGeometry, matLineCyan), [0, 0.18, 0.3], [0, 0, Math.PI / 2], [0.125, 1, 1]],
				[new THREE.Line(lineGeometry, matLineCyan), [0, 0.3, 0.18], [0, - Math.PI / 2, 0], [0.125, 1, 1]]
			],
			XZ: [
				[new THREE.Mesh(new THREE.PlaneGeometry(0.295, 0.295), matMagentaTransparent.clone()), [0.15, 0, 0.15], [- Math.PI / 2, 0, 0]],
				[new THREE.Line(lineGeometry, matLineMagenta), [0.18, 0, 0.3], null, [0.125, 1, 1]],
				[new THREE.Line(lineGeometry, matLineMagenta), [0.3, 0, 0.18], [0, - Math.PI / 2, 0], [0.125, 1, 1]]
			]
		};

		const pickerTranslate = {
			X: [
				[new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0, 1, 4, 1, false), matInvisible), [0.6, 0, 0], [0, 0, - Math.PI / 2]]
			],
			Y: [
				[new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0, 1, 4, 1, false), matInvisible), [0, 0.6, 0]]
			],
			Z: [
				[new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0, 1, 4, 1, false), matInvisible), [0, 0, 0.6], [Math.PI / 2, 0, 0]]
			],
			XYZ: [
				[new THREE.Mesh(new THREE.OctahedronGeometry(0.2, 0), matInvisible)]
			],
			XY: [
				[new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.4), matInvisible), [0.2, 0.2, 0]]
			],
			YZ: [
				[new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.4), matInvisible), [0, 0.2, 0.2], [0, Math.PI / 2, 0]]
			],
			XZ: [
				[new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.4), matInvisible), [0.2, 0, 0.2], [- Math.PI / 2, 0, 0]]
			]
		};

		const helperTranslate = {
			// START: [
			// 	[ new THREE.Mesh( new THREE.OctahedronGeometry( 0.01, 2 ), matHelper ), null, null, null, 'helper' ]
			// ],
			// END: [
			// 	[ new THREE.Mesh( new THREE.OctahedronGeometry( 0.01, 2 ), matHelper ), null, null, null, 'helper' ]
			// ],
			// DELTA: [
			// 	[ new Line2( TranslateHelperGeometry(), matHelper ), null, null, null, 'helper' ]
			// ],
			// X: [
			// 	[ new Line2( lineGeometry, matHelper.clone() ), [ - 1e3, 0, 0 ], null, [ 1e6, 1, 1 ], 'helper' ]
			// ],
			// Y: [
			// 	[ new Line2( lineGeometry, matHelper.clone() ), [ 0, - 1e3, 0 ], [ 0, 0, Math.PI / 2 ], [ 1e6, 1, 1 ], 'helper' ]
			// ],
			// Z: [
			// 	[ new Line2( lineGeometry, matHelper.clone() ), [ 0, 0, - 1e3 ], [ 0, - Math.PI / 2, 0 ], [ 1e6, 1, 1 ], 'helper' ]
			// ]
		};

		const gizmoRotate = {
			X: [
				[new THREE.Line(CircleGeometry(1, 0.5), matLineRed)],
				[new THREE.Mesh(new THREE.OctahedronGeometry(0.04, 0), matRed), [0, 0, 0.99], null, [1, 3, 1]],
			],
			Y: [
				[new THREE.Line(CircleGeometry(1, 0.5), matLineGreen), null, [0, 0, - Math.PI / 2]],
				[new THREE.Mesh(new THREE.OctahedronGeometry(0.04, 0), matGreen), [0, 0, 0.99], null, [3, 1, 1]],
			],
			Z: [
				[new THREE.Line(CircleGeometry(1, 0.5), matLineBlue), null, [0, Math.PI / 2, 0]],
				[new THREE.Mesh(new THREE.OctahedronGeometry(0.04, 0), matBlue), [0.99, 0, 0], null, [1, 3, 1]],
			],
			E: [
				[new THREE.Line(CircleGeometry(1.25, 1), matLineYellowTransparent), null, [0, Math.PI / 2, 0]],
				[new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0, 0.15, 4, 1, false), matLineYellowTransparent), [1.17, 0, 0], [0, 0, - Math.PI / 2], [1, 1, 0.001]],
				[new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0, 0.15, 4, 1, false), matLineYellowTransparent), [- 1.17, 0, 0], [0, 0, Math.PI / 2], [1, 1, 0.001]],
				[new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0, 0.15, 4, 1, false), matLineYellowTransparent), [0, - 1.17, 0], [Math.PI, 0, 0], [1, 1, 0.001]],
				[new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0, 0.15, 4, 1, false), matLineYellowTransparent), [0, 1.17, 0], [0, 0, 0], [1, 1, 0.001]],
			],
			XYZE: [
				[new THREE.Line(CircleGeometry(1, 1), matLineGray), null, [0, Math.PI / 2, 0]]
			]
		};

		const helperRotate = {
			AXIS: [
				[new THREE.Line(lineGeometry, matHelperRotation), [- 1e3, 0, 0], null, [1e6, 1, 1], 'helper']
			]
		};

		const pickerRotate = {
			X: [
				[new THREE.Mesh(new THREE.TorusGeometry(1, 0.1, 4, 24), matInvisible), [0, 0, 0], [0, - Math.PI / 2, - Math.PI / 2]],
			],
			Y: [
				[new THREE.Mesh(new THREE.TorusGeometry(1, 0.1, 4, 24), matInvisible), [0, 0, 0], [Math.PI / 2, 0, 0]],
			],
			Z: [
				[new THREE.Mesh(new THREE.TorusGeometry(1, 0.1, 4, 24), matInvisible), [0, 0, 0], [0, 0, - Math.PI / 2]],
			],
			E: [
				[new THREE.Mesh(new THREE.TorusGeometry(1.25, 0.1, 2, 24), matInvisible)]
			],
			// XYZE: [
			// 	[new THREE.Mesh(new THREE.SphereGeometry(0.7, 10, 8), matInvisible)]
			// ]
		};

		const gizmoScale = {
			X: [
				[new THREE.Mesh(scaleHandleGeometry, matRed), [0.8, 0, 0], [0, 0, - Math.PI / 2]],
				[new THREE.Line(lineGeometry, matLineRed), null, null, [0.8, 1, 1]]
			],
			Y: [
				[new THREE.Mesh(scaleHandleGeometry, matGreen), [0, 0.8, 0]],
				[new THREE.Line(lineGeometry, matLineGreen), null, [0, 0, Math.PI / 2], [0.8, 1, 1]]
			],
			Z: [
				[new THREE.Mesh(scaleHandleGeometry, matBlue), [0, 0, 0.8], [Math.PI / 2, 0, 0]],
				[new THREE.Line(lineGeometry, matLineBlue), null, [0, - Math.PI / 2, 0], [0.8, 1, 1]]
			],
			XY: [
				[new THREE.Mesh(scaleHandleGeometry, matYellowTransparent), [0.85, 0.85, 0], null, [2, 2, 0.2]],
				[new THREE.Line(lineGeometry, matLineYellow), [0.855, 0.98, 0], null, [0.125, 1, 1]],
				[new THREE.Line(lineGeometry, matLineYellow), [0.98, 0.855, 0], [0, 0, Math.PI / 2], [0.125, 1, 1]]
			],
			YZ: [
				[new THREE.Mesh(scaleHandleGeometry, matCyanTransparent), [0, 0.85, 0.85], null, [0.2, 2, 2]],
				[new THREE.Line(lineGeometry, matLineCyan), [0, 0.855, 0.98], [0, 0, Math.PI / 2], [0.125, 1, 1]],
				[new THREE.Line(lineGeometry, matLineCyan), [0, 0.98, 0.855], [0, - Math.PI / 2, 0], [0.125, 1, 1]]
			],
			XZ: [
				[new THREE.Mesh(scaleHandleGeometry, matMagentaTransparent), [0.85, 0, 0.85], null, [2, 0.2, 2]],
				[new THREE.Line(lineGeometry, matLineMagenta), [0.855, 0, 0.98], null, [0.125, 1, 1]],
				[new THREE.Line(lineGeometry, matLineMagenta), [0.98, 0, 0.855], [0, - Math.PI / 2, 0], [0.125, 1, 1]]
			],
			// XYZ: [
			// 	[new THREE.Mesh(new THREE.BoxGeometry(0.125, 0.125, 0.125), matWhiteTransparent.clone()), [0, 0, 0], [0, 0, 0]]
			// ],
			XYZX: [
				[new THREE.Mesh(new THREE.BoxGeometry(0.125, 0.125, 0.125), matWhiteTransparent.clone()), [1.1, 0, 0]],
			],
			XYZY: [
				[new THREE.Mesh(new THREE.BoxGeometry(0.125, 0.125, 0.125), matWhiteTransparent.clone()), [0, 1.1, 0]],
			],
			XYZZ: [
				[new THREE.Mesh(new THREE.BoxGeometry(0.125, 0.125, 0.125), matWhiteTransparent.clone()), [0, 0, 1.1]],
			]
		};

		const pickerScale = {
			X: [
				[new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0, 0.8, 4, 1, false), matInvisible), [0.5, 0, 0], [0, 0, - Math.PI / 2]]
			],
			Y: [
				[new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0, 0.8, 4, 1, false), matInvisible), [0, 0.5, 0]]
			],
			Z: [
				[new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0, 0.8, 4, 1, false), matInvisible), [0, 0, 0.5], [Math.PI / 2, 0, 0]]
			],
			XY: [
				[new THREE.Mesh(scaleHandleGeometry, matInvisible), [0.85, 0.85, 0], null, [3, 3, 0.2]],
			],
			YZ: [
				[new THREE.Mesh(scaleHandleGeometry, matInvisible), [0, 0.85, 0.85], null, [0.2, 3, 3]],
			],
			XZ: [
				[new THREE.Mesh(scaleHandleGeometry, matInvisible), [0.85, 0, 0.85], null, [3, 0.2, 3]],
			],
			// XYZ: [
			// 	[new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.2, 0.2), matInvisible)]
			// ],
			XYZX: [
				[new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.2, 0.2), matInvisible), [1.1, 0, 0]],
			],
			XYZY: [
				[new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.2, 0.2), matInvisible), [0, 1.1, 0]],
			],
			XYZZ: [
				[new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.2, 0.2), matInvisible), [0, 0, 1.1]],
			]
		};

		const helperScale = {
			X: [
				[new THREE.Line(lineGeometry, matHelper.clone()), [- 1e3, 0, 0], null, [1e6, 1, 1], 'helper']
			],
			Y: [
				[new THREE.Line(lineGeometry, matHelper.clone()), [0, - 1e3, 0], [0, 0, Math.PI / 2], [1e6, 1, 1], 'helper']
			],
			Z: [
				[new THREE.Line(lineGeometry, matHelper.clone()), [0, 0, - 1e3], [0, - Math.PI / 2, 0], [1e6, 1, 1], 'helper']
			]
		};

		// Creates an Object3D with gizmos described in custom hierarchy definition.

		const setupGizmo = function(gizmoMap)
		{
			const gizmo = new THREE.Object3D();

			for (const name in gizmoMap)
			{
				for (let i = gizmoMap[name].length; i--;)
				{
					const object = gizmoMap[name][i][0].clone();
					const position = gizmoMap[name][i][1];
					const rotation = gizmoMap[name][i][2];
					const scale = gizmoMap[name][i][3];
					const tag = gizmoMap[name][i][4];

					// name and tag properties are essential for picking and updating logic.
					object.name = name;
					object.tag = tag;

					if (position)
					{
						object.position.set(position[0], position[1], position[2]);
					}

					if (rotation)
					{
						object.rotation.set(rotation[0], rotation[1], rotation[2]);
					}

					if (scale)
					{
						object.scale.set(scale[0], scale[1], scale[2]);
					}

					object.updateMatrix();

					const tempGeometry = object.geometry.clone();
					tempGeometry.applyMatrix4(object.matrix);
					object.geometry = tempGeometry;
					object.renderOrder = Infinity;

					object.position.set(0, 0, 0);
					object.rotation.set(0, 0, 0);
					object.scale.set(1, 1, 1);

					gizmo.add(object);
				}
			}

			return gizmo;
		};

		// Reusable utility variables

		const tempVector = new THREE.Vector3(0, 0, 0);
		const tempEuler = new THREE.Euler();
		const alignVector = new THREE.Vector3(0, 1, 0);
		const zeroVector = new THREE.Vector3(0, 0, 0);
		const lookAtMatrix = new THREE.Matrix4();
		const tempQuaternion = new THREE.Quaternion();
		const tempQuaternion2 = new THREE.Quaternion();
		const identityQuaternion = new THREE.Quaternion();

		const unitX = new THREE.Vector3(1, 0, 0);
		const unitY = new THREE.Vector3(0, 1, 0);
		const unitZ = new THREE.Vector3(0, 0, 1);

		// Gizmo creation

		this.gizmo = {};
		this.picker = {};
		this.helper = {};

		this.add(this.gizmo["translate"] = setupGizmo(gizmoTranslate));
		this.add(this.gizmo["rotate"] = setupGizmo(gizmoRotate));
		this.add(this.gizmo["scale"] = setupGizmo(gizmoScale));
		this.add(this.picker["translate"] = setupGizmo(pickerTranslate));
		this.add(this.picker["rotate"] = setupGizmo(pickerRotate));
		this.add(this.picker["scale"] = setupGizmo(pickerScale));
		this.add(this.helper["translate"] = setupGizmo(helperTranslate));
		this.add(this.helper["rotate"] = setupGizmo(helperRotate));
		this.add(this.helper["scale"] = setupGizmo(helperScale));

		// Pickers should be hidden always

		this.picker["translate"].visible = false;
		this.picker["rotate"].visible = false;
		this.picker["scale"].visible = false;

		// updateMatrixWorld will update transformations and appearance of individual handles

		this.toggleDashedLines = function(dashed)
		{
			gizmoTranslate.X[1][0].material.dashed = dashed;
			gizmoTranslate.Y[1][0].material.dashed = dashed;
			gizmoTranslate.Z[1][0].material.dashed = dashed;
			gizmoTranslate.X[1][0].material.needsUpdate = true;
			gizmoTranslate.Y[1][0].material.needsUpdate = true;
			gizmoTranslate.Z[1][0].material.needsUpdate = true;

			if(dashed)
			{
				gizmoTranslate.X[1][0].material.linewidth = 3.0;
				gizmoTranslate.Y[1][0].material.linewidth = 3.0;
				gizmoTranslate.Z[1][0].material.linewidth = 3.0;
			}
			else
			{
				gizmoTranslate.X[1][0].material.linewidth = 2.0;
				gizmoTranslate.Y[1][0].material.linewidth = 2.0;
				gizmoTranslate.Z[1][0].material.linewidth = 2.0;
			}

			gizmoTranslate.X[1][0].computeLineDistances();
			gizmoTranslate.Y[1][0].computeLineDistances();
			gizmoTranslate.Z[1][0].computeLineDistances();
		}

		window.addEventListener('resize',  () =>
		{
			if(gizmoTranslate.X[1][0].material)
			{
				gizmoTranslate.X[1][0].material.uniforms.resolution.value.set( window.innerWidth, window.innerHeight);
				gizmoTranslate.Y[1][0].material.uniforms.resolution.value.set( window.innerWidth, window.innerHeight);
				gizmoTranslate.Z[1][0].material.uniforms.resolution.value.set( window.innerWidth, window.innerHeight);
			}
		});


		this.updateMatrixWorld = function()
		{
			if(!this.camera) return;
			let space = this.space;

			if (this.mode === 'scale')
			{space = 'local';} // scale always oriented to local rotation

			const quaternion = space === "local" ? this.worldQuaternion : identityQuaternion;

			// Show only gizmos for current transform mode

			this.gizmo["translate"].visible = this.mode === "translate";
			this.gizmo["rotate"].visible = this.mode === "rotate";
			this.gizmo["scale"].visible = this.mode === "scale";

			this.helper["translate"].visible = this.mode === "translate";
			this.helper["rotate"].visible = this.mode === "rotate";
			this.helper["scale"].visible = this.mode === "scale";

			let handles = [];
			if (this.mode !== 'hidden')
			{
				handles = handles.concat(this.picker[this.mode].children);
				handles = handles.concat(this.gizmo[this.mode].children);
				handles = handles.concat(this.helper[this.mode].children);
			}

			for (let i = 0; i < handles.length; i++)
			{
				const handle = handles[i];

				// hide aligned to camera

				handle.visible = true;
				handle.rotation.set(0, 0, 0);
				handle.position.copy(this.worldPosition);

				let factor;

				if (this.camera.isOrthographicCamera)
				{

					factor = (this.camera.top - this.camera.bottom) / this.camera.zoom;

				}
				else
				{

					factor = this.worldPosition.distanceTo(this.cameraPosition) * Math.min(1.9 * Math.tan(Math.PI * this.camera.fov / 360) / this.camera.zoom, 7);

				}

				handle.scale.set(1, 1, 1).multiplyScalar(factor * this.size / 7);
				// TODO: simplify helpers and consider decoupling from gizmo

				if (handle.tag === 'helper')
				{
					handle.visible = false;

					if (handle.name === 'AXIS')
					{
						handle.position.copy(this.worldPositionStart);
						handle.visible = !!this.axis;

						if (this.axis === 'X')
						{
							tempQuaternion.setFromEuler(tempEuler.set(0, 0, 0));
							handle.quaternion.copy(quaternion).multiply(tempQuaternion);
							matHelperRotation.color.set(0xff0000);

							if (Math.abs(alignVector.copy(unitX).applyQuaternion(quaternion).dot(this.eye)) > 0.9)
							{
								handle.visible = false;
							}
						}

						if (this.axis === 'Y')
						{
							tempQuaternion.setFromEuler(tempEuler.set(0, 0, Math.PI / 2));
							handle.quaternion.copy(quaternion).multiply(tempQuaternion);
							matHelperRotation.color.set(0x00ff00);

							if (Math.abs(alignVector.copy(unitY).applyQuaternion(quaternion).dot(this.eye)) > 0.9)
							{
								handle.visible = false;
							}
						}

						if (this.axis === 'Z')
						{
							tempQuaternion.setFromEuler(tempEuler.set(0, Math.PI / 2, 0));
							handle.quaternion.copy(quaternion).multiply(tempQuaternion);
							matHelperRotation.color.set(0x0000ff);

							if (Math.abs(alignVector.copy(unitZ).applyQuaternion(quaternion).dot(this.eye)) > 0.9)
							{
								handle.visible = false;
							}
						}

						if (this.axis === 'XYZE')
						{
							tempQuaternion.setFromEuler(tempEuler.set(0, Math.PI / 2, 0));
							alignVector.copy(this.rotationAxis);
							handle.quaternion.setFromRotationMatrix(lookAtMatrix.lookAt(zeroVector, alignVector, unitY));
							handle.quaternion.multiply(tempQuaternion);
							handle.visible = this.dragging;
						}

						if (this.axis === 'E')
						{
							handle.visible = false;
						}
					}
					else if (handle.name === 'START')
					{
						handle.position.copy(this.worldPositionStart);
						handle.visible = this.dragging;
					}
					else if (handle.name === 'END')
					{
						handle.position.copy(this.worldPosition);
						handle.visible = this.dragging;
					}
					else if (handle.name === 'DELTA')
					{
						handle.position.copy(this.worldPositionStart);
						handle.quaternion.copy(this.worldQuaternionStart);
						tempVector.set(1e-10, 1e-10, 1e-10).add(this.worldPositionStart).sub(this.worldPosition).multiplyScalar(- 1);
						tempVector.applyQuaternion(this.worldQuaternionStart.clone().invert());
						handle.scale.copy(tempVector);
						handle.visible = this.dragging;
					}
					else
					{
						handle.quaternion.copy(quaternion);

						if (this.dragging)
						{
							handle.position.copy(this.worldPositionStart);
						}
						else
						{
							handle.position.copy(this.worldPosition);
						}

						if (this.axis)
						{
							handle.visible = this.axis.search(handle.name) !== - 1;
						}
					}

					// If updating helper, skip rest of the loop
					continue;
				}

				// Align handles to current local or world rotation
				handle.quaternion.copy(quaternion);

				if (this.mode === 'translate' || this.mode === 'scale')
				{
					// Hide translate and scale axis facing the camera
					const AXIS_HIDE_TRESHOLD = 0.99;
					const PLANE_HIDE_TRESHOLD = 0.2;
					const AXIS_FLIP_TRESHOLD = 0.0;

					if (handle.name === 'X' || handle.name === 'XYZX')
					{
						if (Math.abs(alignVector.copy(unitX).applyQuaternion(quaternion).dot(this.eye)) > AXIS_HIDE_TRESHOLD)
						{
							handle.scale.set(1e-10, 1e-10, 1e-10);
							handle.visible = false;

						}
					}

					if (handle.name === 'Y' || handle.name === 'XYZY')
					{
						if (Math.abs(alignVector.copy(unitY).applyQuaternion(quaternion).dot(this.eye)) > AXIS_HIDE_TRESHOLD)
						{
							handle.scale.set(1e-10, 1e-10, 1e-10);
							handle.visible = false;
						}
					}

					if (handle.name === 'Z' || handle.name === 'XYZZ')
					{
						if (Math.abs(alignVector.copy(unitZ).applyQuaternion(quaternion).dot(this.eye)) > AXIS_HIDE_TRESHOLD)
						{
							handle.scale.set(1e-10, 1e-10, 1e-10);
							handle.visible = false;
						}

					}

					if (handle.name === 'XY')
					{
						if (Math.abs(alignVector.copy(unitZ).applyQuaternion(quaternion).dot(this.eye)) < PLANE_HIDE_TRESHOLD)
						{
							handle.scale.set(1e-10, 1e-10, 1e-10);
							handle.visible = false;
						}
					}
					if (handle.name === 'YZ')
					{
						if (Math.abs(alignVector.copy(unitX).applyQuaternion(quaternion).dot(this.eye)) < PLANE_HIDE_TRESHOLD)
						{
							handle.scale.set(1e-10, 1e-10, 1e-10);
							handle.visible = false;
						}
					}
					if (handle.name === 'XZ')
					{
						if (Math.abs(alignVector.copy(unitY).applyQuaternion(quaternion).dot(this.eye)) < PLANE_HIDE_TRESHOLD)
						{
							handle.scale.set(1e-10, 1e-10, 1e-10);
							handle.visible = false;
						}
					}

					// // Flip translate and scale axis ocluded behind another axis
					// if (handle.name.search('X') !== - 1)
					// {
					// 	if (alignVector.copy(unitX).applyQuaternion(quaternion).dot(this.eye) < AXIS_FLIP_TRESHOLD)
					// 	{
					// 		if (handle.tag === 'fwd')
					// 		{
					// 			handle.visible = false;
					// 		}
					// 		else
					// 		{
					// 			handle.scale.x *= - 1;
					// 		}
					// 	}
					// 	else if (handle.tag === 'bwd')
					// 	{
					// 		handle.visible = false;
					// 	}
					// }

					// if (handle.name.search('Y') !== - 1)
					// {
					// 	if (alignVector.copy(unitY).applyQuaternion(quaternion).dot(this.eye) < AXIS_FLIP_TRESHOLD)
					// 	{
					// 		if (handle.tag === 'fwd')
					// 		{
					// 			handle.visible = false;
					// 		}
					// 		else
					// 		{
					// 			handle.scale.y *= - 1;
					// 		}

					// 	}
					// 	else if (handle.tag === 'bwd')
					// 	{
					// 		handle.visible = false;
					// 	}
					// }

					// if (handle.name.search('Z') !== - 1)
					// {
					// 	if (alignVector.copy(unitZ).applyQuaternion(quaternion).dot(this.eye) < AXIS_FLIP_TRESHOLD)
					// 	{
					// 		if (handle.tag === 'fwd')
					// 		{
					// 			handle.visible = false;
					// 		}
					// 		else
					// 		{
					// 			handle.scale.z *= - 1;
					// 		}
					// 	}
					// 	else if (handle.tag === 'bwd')
					// 	{
					// 		handle.visible = false;
					// 	}
					// }
				}
				else if (this.mode === 'rotate')
				{
					// Align handles to current local or world rotation
					tempQuaternion2.copy(quaternion);
					alignVector.copy(this.eye).applyQuaternion(tempQuaternion.copy(quaternion).invert());

					if (handle.name.search("E") !== - 1)
					{
						handle.quaternion.setFromRotationMatrix(lookAtMatrix.lookAt(this.eye, zeroVector, unitY));
					}

					if (handle.name === 'X')
					{
						tempQuaternion.setFromAxisAngle(unitX, Math.atan2(- alignVector.y, alignVector.z));
						tempQuaternion.multiplyQuaternions(tempQuaternion2, tempQuaternion);
						handle.quaternion.copy(tempQuaternion);
					}

					if (handle.name === 'Y')
					{
						tempQuaternion.setFromAxisAngle(unitY, Math.atan2(alignVector.x, alignVector.z));
						tempQuaternion.multiplyQuaternions(tempQuaternion2, tempQuaternion);
						handle.quaternion.copy(tempQuaternion);
					}

					if (handle.name === 'Z')
					{
						tempQuaternion.setFromAxisAngle(unitZ, Math.atan2(alignVector.y, alignVector.x));
						tempQuaternion.multiplyQuaternions(tempQuaternion2, tempQuaternion);
						handle.quaternion.copy(tempQuaternion);
					}
				}

				// Hide disabled axes
				handle.visible = handle.visible && (handle.name.indexOf("X") === - 1 || this.showX);
				handle.visible = handle.visible && (handle.name.indexOf("Y") === - 1 || this.showY);
				handle.visible = handle.visible && (handle.name.indexOf("Z") === - 1 || this.showZ);
				handle.visible = handle.visible && (handle.name.indexOf("E") === - 1 || (this.showX && this.showY && this.showZ));

				// highlight selected axis
				handle.material._opacity = handle.material._opacity || handle.material.opacity;
				handle.material._color = handle.material._color || handle.material.color.clone();

				handle.material.color.copy(handle.material._color);
				handle.material.opacity = handle.material._opacity;

				if (!this.enabled)
				{
					handle.material.opacity *= 0.5;
					handle.material.color.lerp(new THREE.Color(1, 1, 1), 0.5);
				}
				else if (this.axis)
				{
					if (handle.name === this.axis)
					{
						handle.material.opacity = 1.0;
						//handle.material.color.lerp(new THREE.Color(1, 1, 1), 0.5);
					}
					else if (this.axis.split('').some(function(a) {return handle.name === a;}))
					{
						handle.material.opacity = 1.0;
						handle.material.color.lerp(new THREE.Color(1, 1, 1), 0.5);
					}
					else
					{
						handle.material.opacity *= 0.25;
						handle.material.color.lerp(new THREE.Color(1, 1, 1), 0.5);
					}
				}
			}

			THREE.Object3D.prototype.updateMatrixWorld.call(this);
		};
	}
}

TransformControlsGizmo.prototype.isTransformControlsGizmo = true;

const unitX = new THREE.Vector3(1, 0, 0);
const unitY = new THREE.Vector3(0, 1, 0);
const unitZ = new THREE.Vector3(0, 0, 1);

const tempVector = new THREE.Vector3();
const dirVector = new THREE.Vector3();
const alignVector = new THREE.Vector3();
const tempMatrix = new THREE.Matrix4();
const identityQuaternion = new THREE.Quaternion();

class TransformControlsPlane extends THREE.Mesh
{
	constructor()
	{
		super(
			new THREE.PlaneGeometry( 100000, 100000, 2, 2 ),
			new THREE.MeshBasicMaterial( { visible: false, wireframe: true, side: THREE.DoubleSide, transparent: true, opacity: 0.1, toneMapped: false } )
		);

		this.type = 'TransformControlsPlane';
	}

	updateMatrixWorld(force)
	{
		let space = this.space;

		this.position.copy(this.worldPosition);

		if (this.mode === 'scale')
		{space = 'local';} // scale always oriented to local rotation

		unitX.set(1, 0, 0).applyQuaternion(space === "local" ? this.worldQuaternion : identityQuaternion);
		unitY.set(0, 1, 0).applyQuaternion(space === "local" ? this.worldQuaternion : identityQuaternion);
		unitZ.set(0, 0, 1).applyQuaternion(space === "local" ? this.worldQuaternion : identityQuaternion);

		// Align the plane for current transform mode, axis and space.

		alignVector.copy(unitY);

		switch (this.mode)
		{
			case 'translate':
			case 'scale':
				switch (this.axis)
				{

					case 'X':
						alignVector.copy(this.eye).cross(unitX);
						dirVector.copy(unitX).cross(alignVector);
						break;
					case 'Y':
						alignVector.copy(this.eye).cross(unitY);
						dirVector.copy(unitY).cross(alignVector);
						break;
					case 'Z':
						alignVector.copy(this.eye).cross(unitZ);
						dirVector.copy(unitZ).cross(alignVector);
						break;
					case 'XY':
						dirVector.copy(unitZ);
						break;
					case 'YZ':
						dirVector.copy(unitX);
						break;
					case 'XZ':
						alignVector.copy(unitZ);
						dirVector.copy(unitY);
						break;
					case 'XYZ':
					case 'E':
						dirVector.set(0, 0, 0);
						break;
					default:
						//console.warn("unknown axis value: " + this.axis);
						break;
				}
				break;
			case 'rotate':
			default:
			// special case for rotate
				dirVector.set(0, 0, 0);
		}

		if (dirVector.length() === 0)
		{
			// If in rotate mode, make the plane parallel to camera
			this.quaternion.copy(this.cameraQuaternion);
		}
		else
		{
			tempMatrix.lookAt(tempVector.set(0, 0, 0), dirVector, alignVector);

			this.quaternion.setFromRotationMatrix(tempMatrix);
		}

		super.updateMatrixWorld(force);
	}
}

TransformControlsPlane.prototype.isTransformControlsPlane = true;

export { TransformControls };
